import { Component, DoCheck, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';

import { SnackbarService } from '@services/snackbar/snackbar.service';
import { Observable } from 'rxjs';
import { GroupHasFrontProfileService } from '@services/groups/group-has-frontprofile.service';
import { FrontProfilesService } from '@services/front-profiles/front-profiles.service';
import { AddItemsFromList } from '../../dialogs/add-items-from-list/add-items-from-list.component';
import { MatDialog } from '@angular/material/dialog';
import { ProfileService } from '@services/profiles/profile.service';
import { GroupService } from '@services/groups/group.service';
import {
  GroupHasRoleInOrganisationService
} from '@services/groups/group-has-role-in-organisation.service';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';
import { GroupHasSecuredAppService } from '@services/groups/group-has-secured-app.service';
import { PackageService } from '@services/packages/package.service';
import { GroupHasProfileService } from '@services/groups/group-has-profile.service';
import { CredentialsService } from '@services/credentials/credentials.service';
import { OrganisationService } from '@services/organisations/organisation.service';
import { TranslateService } from '@services/translate/translate.service';
import { DialogService } from '@services/dialog/dialog.service';

@Component({
    selector: 'addressbook-edit-form',
    templateUrl: './addressbook-edit-form.component.html',
    styleUrls: ['./addressbook-edit-form.component.scss'],
    providers: [
        ProfileService, GroupService, GroupHasRoleInOrganisationService, PackageInstalledAppsService, GroupHasSecuredAppService,
        PackageService, GroupHasProfileService,
        CredentialsService, OrganisationService, GroupHasFrontProfileService, FrontProfilesService
    ],
    standalone: false
})
export class AddressbookEditFormComponent implements OnInit, DoCheck {
  @Input() organisationId;
  @Input() itemId;

  @Output() changed: EventEmitter<boolean> = new EventEmitter();
  hasChanged = false;

  group: Models.Group;
  chatAdmin = false;
  missingTitle = false;
  addressbookAdmin = false;
  title: string;
  ghrio: Models.GroupHasRoleInOrganisation[];
  packages: Models.Package[];
  history: any;
  systemMessages: any;
  profiles: Models.FrontProfileModel[];
  activeProfiles: Models.FrontProfileModel[];
  ghfp: Models.GroupHasFrontProfile[];
  profileColumns = [{prop: 'lastName'}, {prop: 'firstName'}, {prop: 'email'}, {prop: 'mobile'}];
  headerButtons = [{ icon: 'add', callback: () => { return this.addProfile() }, tooltipTranslation: 'userAdmin.group.addProfile' }];

  constructor(
    private route: ActivatedRoute, private groupService: GroupService, private translate: TranslateService, private ghrioService: GroupHasRoleInOrganisationService,
    private snackbar: SnackbarService, private packageService: PackageService, private dialogService: DialogService, private ghfpService: GroupHasFrontProfileService,
    private profileService: FrontProfilesService, private router: Router, private dialog: MatDialog
  ) {
  }

  @HostListener('window:beforeunload')
  @HostListener('window:beforeunload', ['$event'])
  canDeactivate(): Observable<boolean> | boolean | Promise<boolean> {
    return this.handleBeforeUnload();
  }

  async ngDoCheck() {
    if (this.hasChanged !== !this.handleBeforeUnload()) {
      this.hasChanged = !this.handleBeforeUnload();
      this.changed.emit(this.hasChanged);
    }
  }

  async ngOnInit() {
    this.title = this.translate.get('userAdmin.addressbook.new');
    this.systemMessages = this.translate.get('system');
    this.group = new Models.Group();
    this.group.isPersonal = false;
    if (this.itemId) {
      this.group = await this.groupService.get(this.itemId);
      if ((this.group?.name || '').startsWith('pref_')) {
        this.group.name = this.group.name.split('_').splice(2, this.group.name.split('_').length - 1).join('_');
      }
      this.ghrio = (await this.ghrioService.find(0, 1000, { GroupID: this.itemId })).data;

      const roleMapping = { '47': 'chatAdmin', '52': 'addressbookAdmin' };

      Object.keys(roleMapping).forEach(roleID => {
        const properties = roleMapping[roleID];
        if (Array.isArray(properties)) {
          properties.forEach(prop => this[prop] = !!this.ghrio.find(el => el.RoleID.toString() === roleID));
        } else {
          this[properties] = !!this.ghrio.find(el => el.RoleID.toString() === roleID);
        }
      });
      this.packages = (await this.packageService.find(0, 1000, { OwnerOrganisationID: this.organisationId })).data;
      this.title = this.translate.get('userAdmin.addressbook.title') + ': ' + this.group.name;
      this.ghfp = (await this.ghfpService.find(0, 1000, { GroupID: this.group.GroupID })).data;
      const packageIds = (this.route.snapshot.params['packageId'] ? [this.route.snapshot.params['packageId']] : this.packages).map(pak => pak?._id?.toString() || pak?.toString() || pak);
      this.profiles = (await this.profileService.find(0, 1000, { packageId: { $in: packageIds }, isAnonymous: false })).data;
      this.activeProfiles = this.profiles.filter(profile => this.ghfp.map(el => el.ProfileID.toString()).includes(profile.FrontProfileID.toString()));
    }
    this.initHistory();
  }

  initHistory(): void {
    this.history = {};
    ['group', 'chatAdmin', 'addressbookAdmin', 'activeProfiles'].forEach(field => {
      this.history[field] = JSON.stringify(this[field])
    });
  }

  handleBeforeUnload() {
    return !Object.keys(this?.history || {}).some(key => this.history?.[key] !== JSON.stringify(this?.[key]));
  }

  async save() {
    this.missingTitle = !this.group.name || this.group.name.length < 3;
    if (this.missingTitle) {
      return;
    }
    let res;
    const group = Object.assign({}, this.group);
    group.name = 'pref_' + this.organisationId + '_' + group.name;
    try {
      res = !!this.itemId ? await this.groupService.update(this.itemId, group) : await this.groupService.create(group);
    } catch (err) {
      return this.snackbar.open('Die Gruppe konnte nicht erstellt werden, möglicherweise existiert bereits eine Gruppe mit dem gleichen Namen, versuchen Sie es mit einem anderen Namen.', 10000);
    }
    if (res?.name !== group?.name) {
      return this.snackbar.open('Die Gruppe konnte nicht erstellt werden, möglicherweise existiert bereits eine Gruppe mit dem gleichen Namen, versuchen Sie es mit einem anderen Namen.', 10000);
    }
    await this.toggleRole(res.GroupID, true, 48);
    await this.toggleRole(res.GroupID, this.addressbookAdmin, 52);
    if (!this.itemId) {
      return this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId, 'addressbooks', res.GroupID]);
    }
    const added = this.arrayDifference(this.activeProfiles.map(el => el.FrontProfileID), this.ghfp.map(el => el.ProfileID));
    const removed = this.arrayDifference(this.ghfp.map(el => el.ProfileID), this.activeProfiles.map(el => el.FrontProfileID));
    const promises = [];
    added.forEach(profileId => { promises.push(this.ghfpService.create({ ProfileID: profileId, GroupID: this.group.GroupID })) } );
    removed.map(profileId => this.ghfp.filter(g => g.ProfileID === profileId)).flat().map(el => el.ID).forEach(groupId => { promises.push(this.ghfpService.remove(groupId)); });
    await Promise.all(promises);
    this.initHistory();
    this.snackbar.success('Successful updated Profile');
    this.ngOnInit();
  }

  arrayDifference(arr1, arr2) {
    const set2 = new Set(arr2);
    return arr1.filter(x => !set2.has(x));
  }

  async abort() {
    if (!!this.handleBeforeUnload()) {
      this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId]);
    } else {
      const confirmation = await this.dialogService.confirm(this.systemMessages['general']['pendingChanges']);
      if (confirmation) {
        this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId]);
      }
    }
  }

  async toggleRole(groupId, activate, roleId) {
    const existing = !!this.ghrio ? this.ghrio.find(el => el.RoleID.toString() === roleId.toString()) : null;
    if (activate && !existing) {
      await this.ghrioService.create({ GroupID: groupId, RoleID: roleId, OrganisationID: this.organisationId });
    }
    if (!activate && !!existing) {
      await this.ghrioService.remove(existing.ID.toString());
    }
  }

  async addProfile() {
    const active = this.activeProfiles;
    const all = this.profiles;
    const profileIds = all.filter(el => !active.includes(el)).map(el => el.FrontProfileID);
    let items = [];
    for (let i = 0; i < Math.ceil(profileIds?.length / 50); i++) {
      items = items.concat((await this.profileService.find(0, 50, { FrontProfileID: { $in: profileIds.slice(i * 50, (i + 1) * 50) } })).data);
    }
    const dialogRef = this.dialog.open(AddItemsFromList, { width: '1200px', maxHeight: '90vh', data: { availableItems: items, columns: this.profileColumns, idField: 'FrontProfileID' } });
    const result = await dialogRef.afterClosed().toPromise();
    this.activeProfiles = null;
    result?.additionalItems?.forEach(el => {
      active.push(this.profiles.find(profile => profile.FrontProfileID === el));
    });
    setTimeout(() => {
      this.activeProfiles = active;
    }, 0);
  }

  async removeProfile(event: any) {
    const items = JSON.parse(JSON.stringify(this.activeProfiles.filter(el => el.email !== event.row.email)));
    setTimeout(() => {
      this.activeProfiles = items;
    }, 0);
  }
}
